/**
 * Picked-but-not-yet-uploaded photos that survive a reload.
 *
 * Picks used to live only in React state. A reload -- the Facebook app
 * recreating its WebView, a phone reclaiming a backgrounded tab, an accidental
 * pull-to-refresh -- lost the whole selection, and with 300 photos picked in
 * batches of 100 that is a lot to redo. The order session survives a reload;
 * now the picks do too, as far as storage allows.
 *
 * What is kept: the add-time byte snapshots (see file-snapshot.ts), i.e. the
 * photos whose bytes are already in memory, bounded by the same 80 MB budget.
 * Files beyond it stay live handles only and are not written: copying them
 * would mean reading every one of them in full at pick time, the very cost
 * the snapshot budget exists to cap. The prompt after a reload says how many
 * came back out of how many were picked.
 *
 * A load's picks are offered only once that page is gone. Each page load holds
 * a Web Lock named after it for as long as it lives; a second tab of the same
 * order sees the first tab's lock and leaves its picks alone, instead of
 * offering back photos that tab is uploading right now.
 *
 * Everything here is best effort. IndexedDB is missing in some WebViews,
 * refused in private modes, and can run out of quota; quota and eviction
 * inside the Facebook/Instagram WebViews are unverified. Any failure degrades
 * to the old behaviour (nothing kept) and never reaches the customer.
 */

export interface StoredPick {
  id: string;
  session: string;
  /** The page load that saved it. A reload offers only an EARLIER load's picks. */
  loadId: string;
  seq: number;
  name: string;
  type: string;
  lastModified: number;
  count: number;
  blob: Blob;
  savedAt: number;
  /**
   * Set when this pick's upload started: where to ask storage whether it
   * arrived, and the size it must have. See PendingPicks.sending.
   */
  landedCheck?: LandedCheck;
}

/** A presigned HEAD URL for a pick's upload, and the byte count that was sent. */
export interface LandedCheck {
  headUrl: string;
  size: number;
}

export interface StoredLoad {
  /** `${session}|${loadId}` */
  key: string;
  session: string;
  loadId: string;
  /** Photos picked in that load in total, including those not kept. */
  total: number;
  /**
   * Of those, how many reached storage. They are in the cart already, so they
   * are neither offered back nor reported as missing. Absent on older records.
   */
  landed?: number;
  /**
   * File names of the picks that were neither kept nor landed. No longer
   * written or read (the offer gives a count, never names); still present on
   * records from older loads.
   */
  missing?: string[];
  /**
   * Storage keys this load had presigned for photos that never landed,
   * written the moment the server signed them (it records an upload intent
   * per key then, whether or not the PUT ever starts). Kept on the load
   * rather than the pick so photos over the snapshot budget, which have no
   * pick record, are covered too. A later load that resumes or discards this
   * one sends them to the server, which gives up on those intents (only the
   * ones storage confirms missing). At most UPLOAD_KEYS_MAX. Absent on older
   * records.
   */
  keys?: string[];
  /**
   * The photo size selected when this load last wrote, so a resume can
   * preselect it. Absent on older records.
   */
  photoSizeId?: string;
  /**
   * The photo group (order_photo id) this load's last upload was sending its
   * picks into. A resume sends them back there, so the customer sees one row
   * of 60 rather than "25 + 35". Absent before an upload targeted a group,
   * and on older records.
   */
  orderPhotoId?: string;
  savedAt: number;
}

/** Where a load's picks were headed: see StoredLoad.photoSizeId / orderPhotoId. */
export interface PickTarget {
  photoSizeId?: string;
  orderPhotoId?: string;
}

/** Storage, behind an interface so the logic is testable without IndexedDB. */
export interface PicksBackend {
  putPicks(picks: StoredPick[]): Promise<void>;
  putLoad(meta: StoredLoad): Promise<void>;
  getSession(session: string): Promise<{ loads: StoredLoad[]; picks: StoredPick[] }>;
  deletePicks(ids: string[]): Promise<void>;
  deleteLoads(keys: string[]): Promise<void>;
  /** Loads saved before `before`, any session, for pruning. */
  staleLoads(before: number): Promise<StoredLoad[]>;
  /** Pick ids belonging to these loads. */
  pickIdsOfLoads(loads: StoredLoad[]): Promise<string[]>;
  /** Attach a landed check to a stored pick; nothing if it is not stored. */
  setLandedCheck(id: string, check: LandedCheck): Promise<void>;
  /** Update stored picks' copy counts; ids not stored are skipped. */
  setCounts(updates: { id: string; count: number }[]): Promise<void>;
}

/**
 * The longest rekey() may hold up the upload that awaits it. IndexedDB can be
 * slow to open, or never settle at all in some WebViews; past this the upload
 * goes ahead and the move finishes (or not) on its own.
 */
export const REKEY_TIMEOUT_MS = 2000;

/** Resolves when `work` settles or after `ms`, whichever is first. Never rejects. */
function settleWithin(work: Promise<unknown>, ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    work.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      () => {
        clearTimeout(timer);
        resolve();
      },
    );
  });
}

/** Older than this, a stored selection is dropped instead of offered. */
export const PICKS_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** The order session key: the order id, or "new" before one exists. */
export function pickSessionKey(orderId: string | null | undefined): string {
  return orderId ? `order:${orderId}` : "new";
}

/** Presigned keys kept per load record; well above the largest real batch. */
export const UPLOAD_KEYS_MAX = 5000;

export interface RestorablePicks {
  /** The session the offer was loaded for: the one to adopt from or discard. */
  session: string;
  /** Photos picked in that earlier load that never reached storage. */
  total: number;
  picks: { id: string; file: File; count: number }[];
  /** That load's presigned-but-never-landed storage keys (see StoredLoad.keys). */
  keys: string[];
  /** The size and group they were picked for, as far as that load knew. */
  target: PickTarget;
}

export interface PendingPicks {
  /** Persist these snapshotted picks; `total` is everything picked so far. */
  save(
    session: string,
    picks: { id: string; file: File; count: number }[],
    total: number,
  ): Promise<void>;
  /** The most recent EARLIER page load's picks for this session, or null. */
  load(session: string): Promise<RestorablePicks | null>;
  /**
   * Drop picks saved by THIS page load that are no longer selected (removed,
   * or uploaded), in every session this load wrote to -- picks saved under
   * "new" are uploaded into an order created moments later. Also refreshes
   * the picked total. Picks left by an earlier load are never touched here:
   * they wait for the customer to accept or discard them.
   */
  reconcile(
    selected: ReadonlySet<string>,
    total: number,
    /** Each selected pick's copy count; stored picks whose count changed are updated. */
    counts?: ReadonlyMap<string, number>,
  ): Promise<void>;
  /**
   * Forget picks whose bytes are now in storage. Called per file as it lands,
   * so a tab killed mid-upload never offers back a photo the server already
   * has -- accepting it would put it in the cart twice.
   */
  forget(ids: string[]): Promise<void>;
  /**
   * Note that this pick's bytes are on their way, and how to tell whether
   * they arrived. forget() is the normal way a landed pick leaves the store,
   * but it runs only once the page hears back. A tab killed after the PUT
   * reached storage and before that -- or before forget's own write landed --
   * leaves a pick the server sweep registers anyway; the reload then asks
   * storage about it rather than offering it back as a duplicate print.
   */
  sending(id: string, check: LandedCheck): Promise<void>;
  /**
   * Note the storage keys the server just signed for these picks. The server
   * records an upload intent per key at that moment, so a reload must be able
   * to name them even for a pick whose PUT never started. Kept until the
   * pick lands (forget), and written to this load's records.
   */
  presigned(entries: ReadonlyArray<{ id: string; key: string }>): Promise<void>;
  /**
   * Move this load's picks from one session to another. Used when the upload
   * creates the order: picks saved under "new" must follow it, because after
   * a reload the page asks for `order:<id>` and would never find them.
   */
  rekey(from: string, to: string): Promise<void>;
  /** Forget everything earlier loads left for this session. */
  discardEarlier(session: string): Promise<void>;
  /**
   * Take over restored picks: rewrite their stored records as this load's
   * (under `to`, which may differ from the offer's session `from`), and only
   * then drop what earlier loads left in `from`. Until this succeeds the
   * restored Files may be backed by those very records, so nothing earlier is
   * deleted when it fails. Resolves true once they are this load's.
   */
  adopt(
    from: string,
    to: string,
    picks: ReadonlyArray<{ id: string; count: number }>,
    total: number,
  ): Promise<boolean>;
  /** Drop anything older than PICKS_MAX_AGE_MS, in every session. */
  prune(): Promise<void>;
  /**
   * Note where this load's picks are headed: the selected size, and the group
   * an upload is sending them into. `undefined` leaves a field as it is,
   * `null` clears it. Written to this load's records straight away, and with
   * every later write.
   */
  describe(target: { photoSizeId?: string | null; orderPhotoId?: string | null }): Promise<void>;
}

const randomId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export const newPickId = randomId;

/** Ids of the page loads still open, in this or another tab. */
export type LiveLoads = () => Promise<Set<string>>;

/** Whether a pick's upload is in storage at the size that was sent. */
export type InStorage = (check: LandedCheck) => Promise<boolean>;

/**
 * Ask storage directly, with the same exact-size rule the upload itself uses
 * to call a photo landed. The HEAD URL is presigned for 12 hours, which covers
 * any realistic reload; an expired or unreachable one throws, and the pick is
 * then offered as before.
 */
const headInStorage: InStorage = async ({ headUrl, size }) => {
  const controller = typeof AbortController === "undefined" ? null : new AbortController();
  const timer = controller ? setTimeout(() => controller.abort(), 15000) : null;
  try {
    const res = await fetch(headUrl, { method: "HEAD", signal: controller?.signal });
    if (!res.ok) return false;
    return parseInt(res.headers.get("content-length") ?? "", 10) === size;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export function createPendingPicks(
  backend: PicksBackend | null,
  now: () => number = Date.now,
  loadId: string = randomId(),
  liveLoads: LiveLoads = async () => new Set(),
  inStorage: InStorage = headInStorage,
): PendingPicks {
  let disabled = !backend;
  const mine = new Map<string, Set<string>>();
  // Picks of this load whose bytes reached storage, stored or not: most picks
  // never fit the snapshot budget, and they are not missing either. Kept to
  // the current selection, so a finished batch does not count against the next.
  const landed = new Set<string>();
  const totals = new Map<string, number>();
  let seq = 0;
  // Presigned storage keys by pick id, until the pick lands (see presigned()).
  const presignedKeys = new Map<string, string[]>();
  const unfinishedKeys = (): string[] => {
    const out: string[] = [];
    for (const keys of presignedKeys.values()) {
      for (const key of keys) {
        if (out.length >= UPLOAD_KEYS_MAX) return out;
        out.push(key);
      }
    }
    return out;
  };
  // Copy counts as last written, by pick id, so reconcile writes only changes.
  const storedCounts = new Map<string, number>();
  // Adopted picks a reconcile has not yet seen selected, with how many
  // reconciles have missed them. The first miss may be a debounced reconcile
  // computed from the list before the restore committed; it must not delete
  // the only copy of a restored photo.
  const adoptedUnseen = new Map<string, number>();
  const isMine = (id: string) => {
    for (const ids of mine.values()) if (ids.has(id)) return true;
    return false;
  };

  const guard = async <T,>(fn: (b: PicksBackend) => Promise<T>, fallback: T): Promise<T> => {
    if (disabled || !backend) return fallback;
    try {
      return await fn(backend);
    } catch (error) {
      // Over quota: stop trying for the rest of this page load rather than
      // failing on every batch. Anything else: this call is lost, quietly.
      if ((error as { name?: string })?.name === "QuotaExceededError") disabled = true;
      return fallback;
    }
  };

  const target: PickTarget = {};
  const loadKey = (session: string) => `${session}|${loadId}`;
  // Liveness that cannot be checked counts as "nothing else open": the
  // behaviour before tabs were told apart.
  const othersOpen = () => liveLoads().catch(() => new Set<string>());
  const putLoad = (b: PicksBackend, session: string, total: number, savedAt: number) => {
    totals.set(session, total);
    return b.putLoad({
      key: loadKey(session),
      session,
      loadId,
      total,
      landed: landed.size,
      keys: unfinishedKeys(),
      ...(target.photoSizeId ? { photoSizeId: target.photoSizeId } : {}),
      ...(target.orderPhotoId ? { orderPhotoId: target.orderPhotoId } : {}),
      savedAt,
    });
  };

  const discardEarlierIn = async (b: PicksBackend, session: string) => {
    const { loads, picks } = await b.getSession(session);
    const open = await othersOpen();
    const earlier = (id: string) => id !== loadId && !open.has(id);
    await b.deletePicks(picks.filter((p) => earlier(p.loadId)).map((p) => p.id));
    await b.deleteLoads(loads.filter((l) => earlier(l.loadId)).map((l) => l.key));
  };

  const rekeyNow = (from: string, to: string) =>
    guard(async (b) => {
      if (from === to) return;
      const ids = mine.get(from);
      if (!ids || ids.size === 0) return;
      const { loads, picks } = await b.getSession(from);
      const ours = picks.filter((p) => p.loadId === loadId && ids.has(p.id));
      const load = loads.find((l) => l.loadId === loadId);
      // Records are keyed by id, so rewriting them moves them; the old load
      // record, left without picks for a moment, is never offered.
      await b.putPicks(ours.map((p) => ({ ...p, session: to })));
      totals.delete(from);
      await putLoad(b, to, load?.total ?? ours.length, now());
      await b.deleteLoads([loadKey(from)]);
      const moved = mine.get(to) ?? new Set<string>();
      ids.forEach((id) => moved.add(id));
      mine.set(to, moved);
      mine.delete(from);
    }, undefined);

  return {
    save: (session, picks, total) =>
      guard(async (b) => {
        if (picks.length === 0) return;
        const at = now();
        // Recorded BEFORE the write: a debounced reconcile that runs while
        // the transaction is still open must already see these as ours, or a
        // photo removed in that window would stay stored and come back.
        const ids = mine.get(session) ?? new Set<string>();
        picks.forEach((p) => {
          ids.add(p.id);
          storedCounts.set(p.id, p.count);
        });
        mine.set(session, ids);
        await b.putPicks(
          picks.map((p) => ({
            id: p.id,
            session,
            loadId,
            seq: seq++,
            name: p.file.name,
            type: p.file.type,
            lastModified: p.file.lastModified,
            count: p.count,
            blob: p.file,
            savedAt: at,
          })),
        );
        await putLoad(b, session, total, at);
      }, undefined),

    forget: (forgotten) =>
      guard(async (b) => {
        const wanted = new Set(forgotten.filter(Boolean));
        if (wanted.size === 0) return;
        wanted.forEach((id) => {
          landed.add(id);
          presignedKeys.delete(id);
        });
        // Most landed picks were never stored (over the snapshot budget):
        // nothing to delete for them. The load record below is still
        // rewritten, since its landed count changes.
        const stored = [...wanted].filter(isMine);
        if (stored.length > 0) await b.deletePicks(stored);
        for (const [session, ids] of mine) {
          if (ids.size === 0) continue;
          wanted.forEach((id) => ids.delete(id));
          if (ids.size === 0) await b.deleteLoads([loadKey(session)]);
          else await putLoad(b, session, totals.get(session) ?? ids.size, now());
        }
      }, undefined),

    presigned: (entries) =>
      guard(async (b) => {
        let added = false;
        entries.forEach(({ id, key }) => {
          if (!id || !key) return;
          const keys = presignedKeys.get(id) ?? [];
          if (keys.includes(key)) return;
          keys.push(key);
          presignedKeys.set(id, keys);
          added = true;
        });
        if (!added) return;
        // Written now, not when the PUT starts: the intent already exists.
        for (const [session, ids] of mine) {
          if (ids.size === 0) continue;
          await putLoad(b, session, totals.get(session) ?? ids.size, now());
        }
      }, undefined),

    sending: (id, check) =>
      guard(async (b) => {
        if (!id || !isMine(id)) return;
        await b.setLandedCheck(id, check);
      }, undefined),

    load: (session) =>
      guard(async (b) => {
        const { loads, picks } = await b.getSession(session);
        const cutoff = now() - PICKS_MAX_AGE_MS;
        const open = await othersOpen();
        const latest = loads
          .filter((l) => l.loadId !== loadId && !open.has(l.loadId) && l.savedAt >= cutoff)
          .sort((x, y) => y.savedAt - x.savedAt)
          .find((l) => picks.some((p) => p.loadId === l.loadId));
        if (!latest) return null;
        const stored = picks
          .filter((p) => p.loadId === latest.loadId)
          .sort((x, y) => x.seq - y.seq);
        // Picks that were on their way when the page died: any that reached
        // storage are the server's now (its sweep registers them), so they are
        // dropped for good rather than offered back to be printed twice.
        // Anything storage cannot vouch for stays on offer.
        const arrived = new Set(
          (
            await Promise.all(
              stored
                .filter((p) => p.landedCheck)
                .map(async (p) =>
                  (await inStorage(p.landedCheck as LandedCheck).catch(() => false)) ? p.id : null,
                ),
            )
          ).filter((id): id is string => id !== null),
        );
        if (arrived.size > 0) await b.deletePicks([...arrived]);
        const theirs = stored.filter((p) => !arrived.has(p.id));
        if (theirs.length === 0) return null;
        return {
          session,
          total: Math.max(latest.total - (latest.landed ?? 0) - arrived.size, theirs.length),
          keys: latest.keys ?? [],
          target: {
            ...(latest.photoSizeId ? { photoSizeId: latest.photoSizeId } : {}),
            ...(latest.orderPhotoId ? { orderPhotoId: latest.orderPhotoId } : {}),
          },
          picks: theirs.map((p) => ({
            id: p.id,
            count: p.count,
            file: new File([p.blob], p.name, { type: p.type, lastModified: p.lastModified }),
          })),
        };
      }, null),

    reconcile: (selectedIds, total, counts) =>
      guard(async (b) => {
        [...landed].filter((id) => !selectedIds.has(id)).forEach((id) => landed.delete(id));
        // An adopted pick is spared its first miss only (see adoptedUnseen).
        const spared = new Set<string>();
        adoptedUnseen.forEach((misses, id) => {
          if (selectedIds.has(id)) adoptedUnseen.delete(id);
          else if (misses === 0) {
            adoptedUnseen.set(id, 1);
            spared.add(id);
          } else adoptedUnseen.delete(id);
        });
        if (counts) {
          const changed: { id: string; count: number }[] = [];
          counts.forEach((count, id) => {
            if (isMine(id) && storedCounts.get(id) !== count) changed.push({ id, count });
          });
          if (changed.length > 0) {
            await b.setCounts(changed);
            changed.forEach(({ id, count }) => storedCounts.set(id, count));
          }
        }
        for (const [session, ids] of mine) {
          if (ids.size === 0) continue;
          const gone = [...ids].filter((id) => !selectedIds.has(id) && !spared.has(id));
          if (gone.length > 0) await b.deletePicks(gone);
          gone.forEach((id) => {
            ids.delete(id);
            storedCounts.delete(id);
          });
          if (ids.size === 0) {
            await b.deleteLoads([loadKey(session)]);
          } else {
            await putLoad(b, session, total, now());
          }
        }
      }, undefined),

    // Bounded: the upload awaits this on its critical path, and guard() only
    // catches rejections, not a transaction that never settles.
    rekey: (from, to) =>
      settleWithin(rekeyNow(from, to), REKEY_TIMEOUT_MS),

    discardEarlier: (session) => guard((b) => discardEarlierIn(b, session), undefined),

    adopt: (from, to, restored, total) =>
      guard(async (b) => {
        const wanted = new Map(restored.map((p) => [p.id, p.count]));
        const { picks } = await b.getSession(from);
        const theirs = picks.filter((p) => wanted.has(p.id));
        const at = now();
        // Recorded before the write, as in save(): this load owns them now.
        const ids = mine.get(to) ?? new Set<string>();
        theirs.forEach((p) => {
          ids.add(p.id);
          adoptedUnseen.set(p.id, 0);
        });
        mine.set(to, ids);
        try {
          await b.putPicks(
            theirs.map((p) => ({
              ...p,
              session: to,
              loadId,
              seq: seq++,
              count: wanted.get(p.id) ?? p.count,
              savedAt: at,
            })),
          );
        } catch (error) {
          // Not ours after all: the earlier records are untouched.
          theirs.forEach((p) => {
            ids.delete(p.id);
            adoptedUnseen.delete(p.id);
          });
          throw error;
        }
        theirs.forEach((p) => storedCounts.set(p.id, wanted.get(p.id) ?? p.count));
        await putLoad(b, to, Math.max(total, ids.size), at);
        // Only now is anything earlier dropped; the adopted records carry this
        // load's id, so they are not among them.
        await discardEarlierIn(b, from);
        return true;
      }, false),

    describe: (next) =>
      guard(async (b) => {
        let changed = false;
        // A size on its own (the selector changing) does not move picks an
        // upload is already sending into a group: they keep that group's size.
        const sizeOnly = next.orderPhotoId === undefined;
        (["orderPhotoId", "photoSizeId"] as const).forEach((field) => {
          if (field === "photoSizeId" && sizeOnly && target.orderPhotoId) return;
          const value = next[field];
          if (value === undefined) return;
          const wanted = value || undefined;
          if (target[field] === wanted) return;
          if (wanted) target[field] = wanted;
          else delete target[field];
          changed = true;
        });
        if (!changed) return;
        for (const [session, ids] of mine) {
          if (ids.size === 0) continue;
          await putLoad(b, session, totals.get(session) ?? ids.size, now());
        }
      }, undefined),

    prune: () =>
      guard(async (b) => {
        const stale = (await b.staleLoads(now() - PICKS_MAX_AGE_MS)).filter(
          (l) => l.loadId !== loadId,
        );
        if (stale.length === 0) return;
        await b.deletePicks(await b.pickIdsOfLoads(stale));
        await b.deleteLoads(stale.map((l) => l.key));
      }, undefined),
  };
}

// ---------------------------------------------------------------------------
// IndexedDB backend
// ---------------------------------------------------------------------------

const DB_NAME = "foto-pending-picks";
const DB_VERSION = 1;
const PICKS = "picks";
const LOADS = "loads";

const promisify = <T,>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const done = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
  });

export function indexedDbPicksBackend(): PicksBackend | null {
  let idb: IDBFactory | undefined;
  try {
    idb = typeof indexedDB === "undefined" ? undefined : indexedDB;
  } catch {
    // Some sandboxed WebViews throw on merely touching the global.
    idb = undefined;
  }
  if (!idb) return null;
  const factory = idb;

  let opening: Promise<IDBDatabase> | null = null;
  const db = () => {
    if (!opening) {
      const attempt = new Promise<IDBDatabase>((resolve, reject) => {
        const request = factory.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
          const d = request.result;
          if (!d.objectStoreNames.contains(PICKS)) {
            const picks = d.createObjectStore(PICKS, { keyPath: "id" });
            picks.createIndex("session", "session");
            picks.createIndex("loadId", "loadId");
          }
          if (!d.objectStoreNames.contains(LOADS)) {
            const loads = d.createObjectStore(LOADS, { keyPath: "key" });
            loads.createIndex("session", "session");
            loads.createIndex("savedAt", "savedAt");
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error("IndexedDB open blocked"));
      });
      opening = attempt;
      // A failed open is retried by the next call rather than cached forever.
      attempt.catch(() => {
        if (opening === attempt) opening = null;
      });
    }
    return opening;
  };

  return {
    async putPicks(picks) {
      const tx = (await db()).transaction(PICKS, "readwrite");
      picks.forEach((p) => tx.objectStore(PICKS).put(p));
      await done(tx);
    },
    async putLoad(meta) {
      const tx = (await db()).transaction(LOADS, "readwrite");
      tx.objectStore(LOADS).put(meta);
      await done(tx);
    },
    async getSession(session) {
      const tx = (await db()).transaction([PICKS, LOADS], "readonly");
      const [loads, picks] = await Promise.all([
        promisify(tx.objectStore(LOADS).index("session").getAll(session)),
        promisify(tx.objectStore(PICKS).index("session").getAll(session)),
      ]);
      return { loads: loads as StoredLoad[], picks: picks as StoredPick[] };
    },
    async deletePicks(ids) {
      if (ids.length === 0) return;
      const tx = (await db()).transaction(PICKS, "readwrite");
      ids.forEach((id) => tx.objectStore(PICKS).delete(id));
      await done(tx);
    },
    async deleteLoads(keys) {
      if (keys.length === 0) return;
      const tx = (await db()).transaction(LOADS, "readwrite");
      keys.forEach((key) => tx.objectStore(LOADS).delete(key));
      await done(tx);
    },
    async staleLoads(before) {
      const tx = (await db()).transaction(LOADS, "readonly");
      return (await promisify(
        tx.objectStore(LOADS).index("savedAt").getAll(IDBKeyRange.upperBound(before)),
      )) as StoredLoad[];
    },
    async pickIdsOfLoads(loads) {
      const tx = (await db()).transaction(PICKS, "readonly");
      const index = tx.objectStore(PICKS).index("loadId");
      const keys = await Promise.all(
        [...new Set(loads.map((l) => l.loadId))].map((id) => promisify(index.getAllKeys(id))),
      );
      return keys.flat().map(String);
    },
    async setLandedCheck(id, check) {
      // Read and write in one transaction, so a forget() queued behind it
      // still deletes the pick, and one that ran first is never undone.
      const tx = (await db()).transaction(PICKS, "readwrite");
      // The put is issued from the get's own success callback, not after an
      // await: older WebKit lets a transaction commit across a promise hop.
      const store = tx.objectStore(PICKS);
      const request = store.get(id);
      request.onsuccess = () => {
        const pick = request.result as StoredPick | undefined;
        if (pick) store.put({ ...pick, landedCheck: check });
      };
      await done(tx);
    },
    async setCounts(updates) {
      if (updates.length === 0) return;
      // Same read-then-write-in-callback shape as setLandedCheck.
      const tx = (await db()).transaction(PICKS, "readwrite");
      const store = tx.objectStore(PICKS);
      updates.forEach(({ id, count }) => {
        const request = store.get(id);
        request.onsuccess = () => {
          const pick = request.result as StoredPick | undefined;
          if (pick) store.put({ ...pick, count });
        };
      });
      await done(tx);
    },
  };
}

/**
 * The upload's `onFileLanded` for a list of picks: forget the landed one in
 * the reload store. Returns the call's promise for tests; callers ignore it.
 */
export function forgetLandedPick(
  files: ReadonlyArray<{ pickId?: string }>,
  index: number,
  store: Pick<PendingPicks, "forget"> = pendingPicks(),
): Promise<void> {
  const id = files[index]?.pickId;
  return id ? store.forget([id]) : Promise.resolve();
}

/**
 * The upload's `onFileSending` for a list of picks: record how to check the
 * pick's upload in the reload store. Returns the call's promise for tests;
 * callers ignore it.
 */
export function notePickSending(
  files: ReadonlyArray<{ pickId?: string }>,
  index: number,
  check: LandedCheck,
  store: Pick<PendingPicks, "sending"> = pendingPicks(),
): Promise<void> {
  const id = files[index]?.pickId;
  return id ? store.sending(id, check) : Promise.resolve();
}

/**
 * The upload's `onFilesPresigned` for a list of picks: record each pick's
 * presigned storage key in the reload store. Returns the call's promise for
 * tests; callers ignore it.
 */
export function notePicksPresigned(
  files: ReadonlyArray<{ pickId?: string }>,
  entries: ReadonlyArray<{ index: number; key: string }>,
  store: Pick<PendingPicks, "presigned"> = pendingPicks(),
): Promise<void> {
  const known = entries
    .map(({ index, key }) => ({ id: files[index]?.pickId ?? "", key }))
    .filter((entry) => entry.id && entry.key);
  return known.length ? store.presigned(known) : Promise.resolve();
}

const LOAD_LOCK_PREFIX = "foto-pick-load:";

type LockManagerLike = {
  request(name: string, callback: () => Promise<unknown>): Promise<unknown>;
  query(): Promise<{ held?: { name?: string }[] }>;
};

function lockManager(): LockManagerLike | null {
  try {
    return (navigator as unknown as { locks?: LockManagerLike }).locks ?? null;
  } catch {
    return null;
  }
}

/**
 * Hold this page load's lock until the page goes away; the browser releases
 * it when the tab closes, reloads or is killed. Without Web Locks nothing is
 * held and every earlier load counts as closed.
 *
 * Let go on `pagehide` and taken again on a back-forward restore: a page
 * holding a lock is kept out of the back-forward cache, and Back from Stripe
 * relies on that cache (utils/page-restored.ts).
 */
function holdLoadLock(loadId: string): void {
  const locks = lockManager();
  if (!locks || typeof window === "undefined") return;
  let release: (() => void) | null = null;
  const take = () => {
    if (release) return;
    const held = new Promise<void>((resolve) => {
      release = resolve;
    });
    locks.request(LOAD_LOCK_PREFIX + loadId, () => held).catch(() => {});
  };
  take();
  window.addEventListener("pagehide", () => {
    release?.();
    release = null;
  });
  window.addEventListener("pageshow", (event) => {
    if ((event as PageTransitionEvent).persisted) take();
  });
}

const liveLoadsFromLocks: LiveLoads = async () => {
  const locks = lockManager();
  if (!locks) return new Set();
  const { held = [] } = await locks.query();
  return new Set(
    held
      .map((l) => l.name ?? "")
      .filter((name) => name.startsWith(LOAD_LOCK_PREFIX))
      .map((name) => name.slice(LOAD_LOCK_PREFIX.length)),
  );
};

let shared: PendingPicks | null = null;
/** The page-wide store. Created lazily, browser only. */
export function pendingPicks(): PendingPicks {
  if (!shared) {
    const loadId = randomId();
    holdLoadLock(loadId);
    shared = createPendingPicks(indexedDbPicksBackend(), Date.now, loadId, liveLoadsFromLocks);
  }
  return shared;
}
